import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { callApi, formatMoney, getInlineLoader } from "../Helpers";
import OnePaymentRecord from "../ones/OnePaymentRecord";

const PAYMENT_METHODS = [
  {
    value: "mobile_push",
    en: "Mobile money push",
    sw: "Ombi la malipo kwa simu",
  },
  {
    value: "cash",
    en: "Cash",
    sw: "Fedha taslimu",
  },
  {
    value: "mobile_transfer",
    en: "Mobile money transfer",
    sw: "Hamisho la fedha kwa simu",
  },
  {
    value: "bank_transfer",
    en: "Bank transfer",
    sw: "Hamisho la benki",
  },
];

export default function RequestedServicePaymentOptions({ uuid }) {

  const appContext = useContext(AppContext);

  const [ready, setReady] = useState(false);
  const [profile, setProfile] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("mobile_push");
  const [phone, setPhone] = useState(appContext.currentUserData?.phone || "");
  const [email, setEmail] = useState(appContext.currentUserData?.email || "");
  const [evidenceText, setEvidenceText] = useState("");
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  async function init() {
    setReady(false);
    await getData();
    setReady(true);
  }

  function getData() {
    return new Promise(async resolve => {
      await callApi("get-requested-service-payment-profile", {
        requestedServiceUuid: uuid,
      }).then((response) => {
        if (response.status === 1) {
          setProfile(response.data);

          if (!amount) {
            setAmount(String(response.data.service.availableToPay || ""));
          }
        } else {
          appContext.tellError(response.msg);
        }

        resolve(response);
      })
    })
  }

  function validatePayment() {
    const numericAmount = Number(amount);
    const maxMobilePushAmount = Number(
      appContext.systemParams?.maxMobilePushAmount || 100000
    );

    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      appContext.tellError("Enter a valid payment amount");
      return false;
    }

    if (
      paymentMethod === "mobile_push" &&
      numericAmount > maxMobilePushAmount
    ) {
      appContext.tellError(
        "This amount is above the mobile push limit. Select another payment method."
      );
      return false;
    }

    if (
      ["mobile_transfer", "bank_transfer"].includes(paymentMethod) &&
      !evidenceText.trim() &&
      evidenceFiles.length === 0
    ) {
      appContext.tellError("Add evidence text or an evidence screenshot");
      return false;
    }

    return true;
  }

  function initializePayment() {
    return new Promise(async resolve => {
      await callApi("initialize-payment", {
        requestedServiceUuid: uuid,
        paymentMethod,
        amount,
      }).then(resolve)
    })
  }

  function submitPayment(paymentUuid) {
    return new Promise(async resolve => {
      await callApi("submit-payment", {
        paymentUuid,
        customerPhone: phone,
        customerEmail: email,
        evidenceText,
        evidenceFiles,
      }).then(resolve)
    })
  }

  function cancelPayment(paymentUuid) {
    return new Promise(async resolve => {
      await callApi("cancel-payment", {
        paymentUuid,
      }).then(resolve)
    })
  }

  function clearPaymentEvidence() {
    setEvidenceText("");
    setEvidenceFiles([]);
  }

  function tellPaymentSubmitted() {
    if (paymentMethod === "mobile_push") {
      appContext.tellMessage(
        "Payment request sent. Check your phone and enter your mobile money PIN."
      );
    } else {
      appContext.tellMessage(
        "Payment submitted. Please wait for administrator confirmation."
      );
    }
  }

  async function makePayment() {
    if (!validatePayment()) {
      return;
    }

    setSubmitting(true);

    const initializationResponse = await initializePayment();

    if (initializationResponse.status !== 1) {
      appContext.tellError(initializationResponse.msg);
      setSubmitting(false);
      return;
    }

    const paymentUuid = initializationResponse.data.paymentUuid;
    const submissionResponse = await submitPayment(paymentUuid);

    if (submissionResponse.status === 1) {
      tellPaymentSubmitted();
      clearPaymentEvidence();
      await getData();
    } else {
      await cancelPayment(paymentUuid);
      appContext.tellError(submissionResponse.msg);
    }

    setSubmitting(false);
  }

  function confirmPaymentSubmission() {
    if (!validatePayment()) {
      return;
    }

    appContext.activateDialog({
      message: `Submit a ${String(paymentMethod).replaceAll("_", " ")} payment of TZS ${formatMoney(amount)}?`,
      onConfirm: makePayment,
    })
  }

  useEffect(() => {
    init();
  }, [uuid])

  useEffect(() => {
    const maxMobilePushAmount = Number(
      appContext.systemParams?.maxMobilePushAmount || 100000
    );

    if (
      paymentMethod === "mobile_push" &&
      Number(amount) > maxMobilePushAmount
    ) {
      setPaymentMethod("mobile_transfer");
    }
  }, [amount, paymentMethod, appContext.systemParams])

  useEffect(() => {
    appContext.setShowOverlayLoader(submitting);

    return () => {
      appContext.setShowOverlayLoader(false);
    }
  }, [submitting])

  if (ready) {
    if (!profile) {
      return (
        <div className="container py-4">
          <p>Service payment profile unavailable.</p>
        </div>
      )
    }

    const service = profile.service;
    const settings = appContext.systemParams || {};
    const isSwahili = appContext.language === "sw";
    const availableAmount = Number(service.availableToPay || 0);
    const maxMobilePushAmount = Number(settings.maxMobilePushAmount || 100000);
    const title = isSwahili
      ? service.titleSw || service.titleEn
      : service.titleEn;
    const canMakePayment = (
      availableAmount > 0 &&
      service.processingStatus !== "cancelled"
    );

    return (
      <div
        className="container"
        style={{
          paddingTop: "20px",
          paddingBottom: "80px",
        }}
      >
        <h5 className="font-bold">
          {title}
        </h5>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-6 mb-2">
                <small className="text-muted">Total</small>
                <br />
                <b>TZS {formatMoney(service.quotedAmount)}</b>
              </div>

              <div className="col-6 mb-2">
                <small className="text-muted">Confirmed</small>
                <br />
                <b>TZS {formatMoney(service.paidAmount)}</b>
              </div>

              <div className="col-6">
                <small className="text-muted">Balance</small>
                <br />
                <b>TZS {formatMoney(service.balanceAmount)}</b>
              </div>

              <div className="col-6">
                <small className="text-muted">Available now</small>
                <br />
                <b>TZS {formatMoney(availableAmount)}</b>
              </div>
            </div>

            {
              Number(service.reservedAmount) > 0
                ? <small className="text-muted d-block mt-2">
                  TZS {formatMoney(service.reservedAmount)} is awaiting confirmation.
                </small>
                : ""
            }
          </div>
        </div>

        {
          canMakePayment
            ? <div className="card mb-3">
              <div className="card-body">
                <h6 className="font-semi-bold">
                  {isSwahili ? "Fanya malipo" : "Make a payment"}
                </h6>

                <div className="mb-3">
                  <label className="form-label">Amount (TZS)</label>
                  <input
                    className="form-control"
                    type="number"
                    min="1"
                    max={availableAmount}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Payment method</label>
                  <select
                    className="form-control"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    {
                      PAYMENT_METHODS.map((item) => (
                        <option
                          value={item.value}
                          key={item.value}
                          disabled={
                            item.value === "mobile_push" &&
                            Number(amount) > maxMobilePushAmount
                          }
                        >
                          {isSwahili ? item.sw : item.en}
                        </option>
                      ))
                    }
                  </select>
                </div>

                {
                  paymentMethod === "mobile_push"
                    ? <MobilePushFields
                      maxMobilePushAmount={maxMobilePushAmount}
                      phone={phone}
                      setPhone={setPhone}
                      email={email}
                      setEmail={setEmail}
                    />
                    : ""
                }

                {
                  paymentMethod === "cash"
                    ? <div className="mb-3">
                      <label className="form-label">
                        Optional receipt or evidence
                      </label>
                      <input
                        className="form-control"
                        type="file"
                        accept="image/jpeg,image/png,application/pdf"
                        onChange={(e) => {
                          setEvidenceFiles(Array.from(e.target.files || []));
                        }}
                      />
                    </div>
                    : ""
                }

                {
                  paymentMethod === "mobile_transfer"
                    ? <>
                      <div className="alert alert-info">
                        <b>{settings.mobilePaymentNetwork || "Mobile network"}</b>
                        <br />
                        {settings.mobilePaymentNumber || "Number not configured"}
                        <br />
                        {settings.mobilePaymentName || ""}
                      </div>

                      <EvidenceFields
                        text={evidenceText}
                        setText={setEvidenceText}
                        setFiles={setEvidenceFiles}
                      />
                    </>
                    : ""
                }

                {
                  paymentMethod === "bank_transfer"
                    ? <>
                      <div className="alert alert-info">
                        <b>{settings.bankAccountProvider || "Bank"}</b>
                        <br />
                        {settings.bankAccountNumber || "Account not configured"}
                        <br />
                        {settings.bankAccountName || ""}
                      </div>

                      <EvidenceFields
                        text={evidenceText}
                        setText={setEvidenceText}
                        setFiles={setEvidenceFiles}
                      />
                    </>
                    : ""
                }

                <button
                  className="btn btn-primary w-100"
                  disabled={submitting}
                  onClick={confirmPaymentSubmission}
                >
                  {submitting ? "Submitting..." : "Submit payment"}
                </button>
              </div>
            </div>
            : ""
        }

        <h6 className="font-semi-bold mt-4">
          {isSwahili ? "Historia ya malipo" : "Payment history"}
        </h6>

        {
          (profile.payments || []).map((item) => (
            <OnePaymentRecord
              data={item}
              key={item.uuid}
            />
          ))
        }

        {
          profile.payments?.length === 0
            ? <p className="text-muted">
              No payments submitted yet.
            </p>
            : ""
        }
      </div>
    )
  } else {
    return (
      <div style={{ padding: "40px" }}>
        {getInlineLoader()}
      </div>
    )
  }
}

function MobilePushFields({
  maxMobilePushAmount,
  phone,
  setPhone,
  email,
  setEmail,
}) {
  return (
    <>
      <div className="alert alert-info">
        Mobile push supports up to TZS {formatMoney(maxMobilePushAmount)} per attempt.
        After submitting, check your phone and enter your PIN.
      </div>

      <div className="mb-3">
        <label className="form-label">Mobile money number</label>
        <input
          className="form-control"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Email</label>
        <input
          className="form-control"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
    </>
  )
}

function EvidenceFields({ text, setText, setFiles }) {
  return (
    <>
      <div className="mb-3">
        <label className="form-label">
          Transaction message or reference
        </label>
        <textarea
          className="form-control"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>

      <div className="mb-3">
        <label className="form-label">
          Evidence screenshot or PDF
        </label>
        <input
          className="form-control"
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          onChange={(e) => {
            setFiles(Array.from(e.target.files || []));
          }}
        />
      </div>
    </>
  )
}
